import { Link, useLocation } from 'react-router';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@bill-alps/ui';
import { withPeriodParams } from '@/lib/period/period-params';

interface SidebarNavItemProps {
  to: string;
  icon: LucideIcon;
  label: string;
  isCollapsed: boolean;
  onNavigate?: () => void;
  className?: string;
  /** Match `to` exactly — for parents whose child route is its own nav item. */
  end?: boolean;
}

export function SidebarNavItem({
  to,
  icon: Icon,
  label,
  isCollapsed,
  onNavigate,
  className,
  end = false,
}: SidebarNavItemProps) {
  const { pathname, search } = useLocation();
  const isActive = pathname === to || (!end && pathname.startsWith(to + '/'));

  return (
    // Single choke point for the sidebar: the selected period follows the
    // reader across the pages it filters, and is dropped everywhere else.
    <Link
      to={withPeriodParams(to, search)}
      onClick={onNavigate}
      title={isCollapsed ? label : undefined}
      className={cn(
        className,
        'flex items-center gap-3 rounded-xl border-2 px-3 py-2.5 transition-all duration-200 ease-spring',
        isActive
          ? 'bg-riso-pink text-white font-semibold shadow-[3px_3px_0_var(--border)] border-riso-pink'
          : 'border-transparent hover:bg-muted/50',
      )}
    >
      <Icon className="size-5 shrink-0" />
      <span
        className={cn(
          'overflow-hidden whitespace-nowrap transition-all duration-300 ease-spring',
          isCollapsed ? 'max-w-0 opacity-0' : 'max-w-[180px] opacity-100',
        )}
      >
        {label}
      </span>
    </Link>
  );
}
